import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Gingerbread man — a fresh-baked cookie person: a warm brown rounded body,
// icing trim at the wrists and ankles, gumdrop buttons, an icing smile and big
// friendly eyes. Built flat and chunky like a real cookie (about 0.7 m tall,
// roughly two heads), standing on stubby rounded legs.
//
// Frame: faces +Z (where the icing smile is), sits on the floor (y = 0).
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"  : the cookie torso with its gumdrop buttons. Pivot at the hips
//               so it rocks side to side in the dance.
//   - "head"  : the round cookie head with the whole face. Pivot at the neck.
//   - "arm-l" / "arm-r" : the stubby cookie arms with their icing cuffs.
//               Pivot at each shoulder so they swing like real arms.
// The legs stay planted (unnamed, merged into the static mesh).

const GINGER = "#b26a31";      // warm baked gingerbread
const ICING = "#f6efe3";       // cream icing trim, smile and eye whites
const CANDY_RED = "#d94f43";   // red gumdrop buttons
const CANDY_TEAL = "#3fa8a0";  // teal gumdrop button
const CHEEK = "#d07a4a";       // toasted rosy cheeks
const COAL = "#2c2f36";        // deep charcoal pupils
const SPARK = "#fdfbf6";       // eye highlights

// ── frame heights ────────────────────────────────────────────────────────────
const HIP_Y = 0.20;            // the body rocks about the hips (leg tops)
const TORSO_CY = 0.345;        // torso centre (r 0.16, squashed flat)
const NECK_Y = 0.50;           // the head pivots here
const HEAD_CY = 0.585;         // head centre (r 0.14, squashed flat)
const SHOULDER_Y = 0.47;       // arm hinge height
const SHOULDER_X = 0.13;       // arm hinge offset from the middle
const ARM_DROOP = 0.34;        // how far the arms angle down from horizontal

const BODY_PIVOT: [number, number, number] = [0, HIP_Y, 0];
const HEAD_PIVOT: [number, number, number] = [0, NECK_Y, 0];
const SHOULDER_L: [number, number, number] = [-SHOULDER_X, SHOULDER_Y, 0];
const SHOULDER_R: [number, number, number] = [SHOULDER_X, SHOULDER_Y, 0];

// One stubby cookie arm angled out-and-down, with an icing cuff at the wrist.
function cookieArm(name: string, side: number): Part[] {
  const rot: [number, number, number] = [0, 0, -side * (Math.PI / 2 + ARM_DROOP)];
  return [
    { geometry: new THREE.CapsuleGeometry(0.05, 0.13, 4, 10), color: GINGER,
      position: [side * 0.21, 0.435, 0], rotation: rot, name },
    // Icing cuff ringing the wrist.
    { geometry: new THREE.CylinderGeometry(0.053, 0.053, 0.032, 12), color: ICING,
      position: [side * 0.285, 0.408, 0], rotation: rot, name },
  ];
}

// One stubby cookie leg (static) with an icing cuff at the ankle.
function cookieLeg(side: number): Part[] {
  return [
    { geometry: new THREE.CapsuleGeometry(0.055, 0.10, 4, 10), color: GINGER,
      position: [side * 0.075, 0.105, 0] },
    { geometry: new THREE.CylinderGeometry(0.058, 0.058, 0.035, 12), color: ICING,
      position: [side * 0.075, 0.055, 0] },
  ];
}

function gingerbreadParts(): Part[] {
  return [
    ...cookieLeg(-1),
    ...cookieLeg(1),

    // ── the cookie torso, flat like a real biscuit ──────────────────────────
    { geometry: new THREE.SphereGeometry(0.16, 18, 14), color: GINGER,
      position: [0, TORSO_CY, 0], scale: [1, 1.1, 0.55], name: "body" },
    // Gumdrop buttons down the front.
    { geometry: new THREE.SphereGeometry(0.027, 10, 8), color: CANDY_RED,
      position: [0, 0.44, 0.078], scale: [1, 0.85, 0.7], name: "body" },
    { geometry: new THREE.SphereGeometry(0.027, 10, 8), color: CANDY_TEAL,
      position: [0, 0.365, 0.085], scale: [1, 0.85, 0.7], name: "body" },
    { geometry: new THREE.SphereGeometry(0.027, 10, 8), color: CANDY_RED,
      position: [0, 0.29, 0.078], scale: [1, 0.85, 0.7], name: "body" },

    ...cookieArm("arm-l", -1),
    ...cookieArm("arm-r", 1),

    // ── the round cookie head + face ────────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.14, 18, 14), color: GINGER,
      position: [0, HEAD_CY, 0], scale: [1, 1, 0.6], name: "head" },
    // Big friendly eyes: icing whites, coal pupils set forward, tiny sparkles.
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: ICING,
      position: [-0.055, 0.605, 0.06], name: "head" },
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: ICING,
      position: [0.055, 0.605, 0.06], name: "head" },
    { geometry: new THREE.SphereGeometry(0.02, 8, 6), color: COAL,
      position: [-0.053, 0.607, 0.078], name: "head" },
    { geometry: new THREE.SphereGeometry(0.02, 8, 6), color: COAL,
      position: [0.053, 0.607, 0.078], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: SPARK,
      position: [-0.046, 0.615, 0.093], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: SPARK,
      position: [0.06, 0.615, 0.093], name: "head" },
    // Toasted rosy cheeks.
    { geometry: new THREE.SphereGeometry(0.021, 8, 6), color: CHEEK,
      position: [-0.088, 0.57, 0.055], name: "head" },
    { geometry: new THREE.SphereGeometry(0.021, 8, 6), color: CHEEK,
      position: [0.088, 0.57, 0.055], name: "head" },
    // Icing smile: an upward arc of piped dots.
    { geometry: new THREE.SphereGeometry(0.015, 6, 5), color: ICING,
      position: [-0.058, 0.55, 0.072], name: "head" },
    { geometry: new THREE.SphereGeometry(0.015, 6, 5), color: ICING,
      position: [-0.022, 0.532, 0.081], name: "head" },
    { geometry: new THREE.SphereGeometry(0.015, 6, 5), color: ICING,
      position: [0.022, 0.532, 0.081], name: "head" },
    { geometry: new THREE.SphereGeometry(0.015, 6, 5), color: ICING,
      position: [0.058, 0.55, 0.072], name: "head" },
  ];
}

export const gingerbreadMan = defineModel({
  id: "gingerbread-man",
  batchId: "mess",
  name: "Gingerbread Man",
  tags: ["mess", "cute", "animated", "character", "gingerbread", "cookie", "sweet", "mascot"],
  summary: "A fresh-baked gingerbread cookie person with icing trim, gumdrop buttons and a happy dance.",
  description:
    "A gingerbread cookie person: a warm brown, biscuit-flat rounded body with " +
    "icing cuffs at the wrists and ankles, red and teal gumdrop buttons, big " +
    "friendly eyes, toasted cheeks and a piped icing smile. The body, head and " +
    "both arms are separately posable; it ships an idle breathing bob and a " +
    "happy side-to-side dance with swinging arms.",
  author: "models",
  footprint: 0.35,
  tiles: { w: 1, h: 1 },
  parts: gingerbreadParts,
  animations: [
    // 1. Idle — a soft, fresh-out-of-the-oven breath: torso and head lift a
    //    touch while the arms sway gently at the sides.
    {
      id: "idle",
      label: "Idle bob",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.018, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.4, value: [-0.03, 0, 0], ease: "sine" },
            { t: 2.8, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0.04], ease: "sine" },
            { t: 1.4, value: [0, 0, 0.11], ease: "sine" },
            { t: 2.8, value: [0, 0, 0.04], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, -0.04], ease: "sine" },
            { t: 1.4, value: [0, 0, -0.11], ease: "sine" },
            { t: 2.8, value: [0, 0, -0.04], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Dance — the happy cookie dance: the torso rocks side to side about the
    //    hips with a little bounce, the head tilts in counter-phase and the arms
    //    swing up alternately. Because the rig is flat, the head and arm tracks
    //    carry a matching x-sway so they ride the lean instead of detaching.
    {
      id: "dance",
      label: "Happy dance",
      duration: 1.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.45, value: [0, 0, 0.15], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, -0.15], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.225, value: [0, 0.016, 0], ease: "sine" },
            { t: 0.45, value: [0, 0, 0], ease: "sine" },
            { t: 0.675, value: [0, 0.016, 0], ease: "sine" },
            { t: 0.9, value: [0, 0, 0], ease: "sine" },
            { t: 1.125, value: [0, 0.016, 0], ease: "sine" },
            { t: 1.35, value: [0, 0, 0], ease: "sine" },
            { t: 1.575, value: [0, 0.016, 0], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        // The head rides the lean (x-sway matches the torso rock at head
        // height) and tilts the other way for a cheeky counterpose.
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.45, value: [-0.048, 0.012, 0], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.35, value: [0.048, 0.012, 0], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.45, value: [0, 0, -0.12], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, 0.12], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        // Arms swing up alternately, carrying the same lean sway.
        {
          target: "arm-l", pivot: SHOULDER_L,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.45, value: [-0.038, 0.008, 0], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.35, value: [0.038, 0.008, 0], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0.1], ease: "easeInOut" },
            { t: 0.45, value: [0, 0, -0.85], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0.1], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, 0.25], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0.1], ease: "easeInOut" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.45, value: [-0.038, 0.008, 0], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.35, value: [0.038, 0.008, 0], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, -0.1], ease: "easeInOut" },
            { t: 0.45, value: [0, 0, -0.25], ease: "easeInOut" },
            { t: 0.9, value: [0, 0, -0.1], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, 0.85], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, -0.1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
